import { useEffect, useMemo, useState } from 'react'
import { format, isValid, parseISO, subDays, startOfYear } from 'date-fns'
import { Bar, BarChart, CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart'
import type { ChartConfig } from '@/components/ui/chart'
import {
  useDemographicsAge,
  useFinance,
  useRegistrations,
  // useTopResources,
  useListAllPastResource,
  useDeletePastResource,
  useGetTrainingNeeds,
} from '@/utils/api/membership-service'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import type { DemographicsSlice } from '@/utils/api/membership-service/models/demographics-slice'
import type { FinancialReport } from '@/utils/api/membership-service/models/financial-report'
import type { RegistrationStats } from '@/utils/api/membership-service/models/registration-stats'
import type { TrainingNeedsReport } from '@/utils/api/membership-service/models/training-needs-report'
import { AlertTriangle, BarChart3, ChartPie, LineChart as LineChartIcon, Target } from 'lucide-react'
import { cn } from '@/lib/utils'

type DateRange = {
  from: string
  to: string
}

const today = new Date()
const defaultTo = format(today, 'yyyy-MM-dd')
const defaultFrom = format(subDays(today, 29), 'yyyy-MM-dd')
const initialRange: DateRange = {
  from: defaultFrom,
  to: defaultTo,
}

const CATEGORY_COLOR_PALETTE = [
  'var(--color-chart-1)',
  'var(--color-chart-2)',
  'var(--color-chart-3)',
  'var(--color-chart-4)',
  'var(--color-chart-5)',
]

function isRangeValid({ from, to }: DateRange): boolean {
  if (!from || !to) return false
  const fromDate = parseISO(from)
  const toDate = parseISO(to)
  if (!isValid(fromDate) || !isValid(toDate)) return false
  return fromDate.getTime() <= toDate.getTime()
}

function formatCurrency(value: number, formatter: Intl.NumberFormat): string {
  return formatter.format(Math.max(0, Number.isFinite(value) ? value : 0))
}

function buildFinanceChartData(rows: FinancialReport[]) {
  return rows.map((row) => {
    const membershipRevenue = row.membershipRevenue ?? 0
    const eventRevenue = row.eventRevenue ?? 0
    const total = row.total ?? membershipRevenue + eventRevenue

    return {
      period: row.period ?? 'Unknown',
      membershipRevenue,
      eventRevenue,
      total,
    }
  })
}

function buildDemographicsChartData(rows: DemographicsSlice[]) {
  const fallbackDimensions = ['18-25', '26-35', '36-45', '46-55', '56+']

  return rows.length
    ? rows
    : fallbackDimensions.map((dimension) => ({ dimension, count: 0 }))
}

type RegistrationCategorySlice = {
  category: string
  registrations: number
}

type RegistrationCategoryChart = {
  data: Array<{ period: string } & Record<string, number>>
  categories: Array<{ key: string; label: string }>
}

type TrainingNeedCloudSlice = {
  label: string
  count: number
  weight: number
}

// type NormalizedTopResource = {
//   id: string
//   label: string
//   resourceType: string
//   count: number
//   lastAccessed?: string
// }

function buildTrainingNeedsCloudData(rows: TrainingNeedsReport[]): TrainingNeedCloudSlice[] {
  if (!rows.length) return []
  const counts = rows.map((item) => item.count ?? 0)
  const max = Math.max(...counts)
  const min = Math.min(...counts)
  const spread = Math.max(max - min, 1)
  const minScale = 0.85
  const maxScale = 2

  return rows.map((item) => {
    const count = item.count ?? 0
    const normalized = (count - min) / spread
    const weight = minScale + normalized * (maxScale - minScale)

    return {
      label: item.need ?? 'Unknown need',
      count,
      weight,
    }
  })
}

function normalizeRegistrationCategorySlice(entry: unknown): RegistrationCategorySlice | null {
  if (!entry || typeof entry !== 'object') return null
  const record = entry as Record<string, unknown>
  const category =
    typeof record.category === 'string'
      ? record.category
      : typeof record.categoryName === 'string'
        ? record.categoryName
        : typeof record.membershipCategory === 'string'
          ? record.membershipCategory
          : typeof record.name === 'string'
            ? record.name
            : undefined
  if (!category) return null

  const valueCandidate =
    record.registrations ??
    record.count ??
    record.volume ??
    record.value ??
    record.total ??
    record.members ??
    record.uniqueUsers

  const registrations =
    typeof valueCandidate === 'number' ? valueCandidate : Number(valueCandidate ?? 0)

  return {
    category,
    registrations: Number.isFinite(registrations) ? registrations : 0,
  }
}

function buildRegistrationCategoryChartData(rows: RegistrationStats[]): RegistrationCategoryChart {
  const periodMap = new Map<string, Record<string, number | string>>()
  const labelToKey = new Map<string, string>()
  let categoryIndex = 0

  const paletteKeys: Array<{ key: string; label: string }> = []

  const ensureCategoryKey = (label: string) => {
    let key = labelToKey.get(label)
    if (!key) {
      key = `registration_category_${categoryIndex++}`
      labelToKey.set(label, key)
      paletteKeys.push({ key, label })
    }
    return key
  }

  rows.forEach((row) => {
    const period = row.period ?? 'Unknown'
    if (!periodMap.has(period)) {
      periodMap.set(period, { period })
    }
    const target = periodMap.get(period)!
    const dynamicRow = row as Record<string, unknown>
    const candidateArrays = [
      dynamicRow.membershipCategoryBreakdown,
      dynamicRow.categoryBreakdown,
      dynamicRow.registrationsByCategory,
      dynamicRow.categorySlices,
      dynamicRow.registrationBreakdown,
    ].filter(Array.isArray) as unknown[][]

    const candidateRecords = [
      dynamicRow.categoryTotals,
      dynamicRow.registrationTotals,
    ].filter((value): value is Record<string, unknown> => Boolean(value) && typeof value === 'object')

    const slices: RegistrationCategorySlice[] = []

    candidateArrays.forEach((entries) => {
      entries.forEach((entry) => {
        const normalized = normalizeRegistrationCategorySlice(entry)
        if (normalized) {
          slices.push(normalized)
        }
      })
    })

    candidateRecords.forEach((record) => {
      Object.entries(record).forEach(([category, value]) => {
        const normalized = normalizeRegistrationCategorySlice(
          typeof value === 'object'
            ? { category, ...(value as Record<string, unknown>) }
            : { category, registrations: value },
        )
        if (normalized) {
          slices.push(normalized)
        }
      })
    })

    if (!slices.length && typeof row.registrations === 'number') {
      slices.push({
        category: 'Unspecified',
        registrations: row.registrations ?? 0,
      })
    }

    slices.forEach((slice) => {
      const key = ensureCategoryKey(slice.category)
      const current = typeof target[key] === 'number' ? (target[key] as number) : 0
      target[key] = current + slice.registrations
    })
  })

  const orderedData = Array.from(periodMap.values()).sort((a, b) => {
    const aPeriod = typeof a.period === 'string' ? a.period : ''
    const bPeriod = typeof b.period === 'string' ? b.period : ''
    return aPeriod.localeCompare(bPeriod)
  }) as Array<{ period: string } & Record<string, number>>

  const categories = paletteKeys
  return {
    data: orderedData,
    categories,
  }
}

type CategoryFinanceSlice = {
  category: string
  subscriptionAmount: number
  members: number
  institution?: string
}

type CategorySubscriptionSummary = {
  category: string
  subscriptionAmount: number
  members: number
}

function normalizeCategorySlice(
  slice: unknown,
  fallbackInstitution?: string,
): CategoryFinanceSlice | null {
  if (!slice || typeof slice !== 'object') return null
  const record = slice as Record<string, unknown>

  const category =
    typeof record.category === 'string'
      ? record.category
      : typeof record.categoryName === 'string'
        ? record.categoryName
        : typeof record.membershipCategory === 'string'
          ? record.membershipCategory
          : typeof record.name === 'string'
            ? record.name
            : undefined

  if (!category) return null

  const amountCandidate =
    record.subscriptionAmount ??
    record.amount ??
    record.revenue ??
    record.total ??
    record.subscriptions ??
    record.value
  const membersCandidate =
    record.members ??
    record.memberCount ??
    record.count ??
    record.memberships ??
    record.volume

  const amountValue =
    typeof amountCandidate === 'number'
      ? amountCandidate
      : Number(amountCandidate ?? 0)
  const membersValue =
    typeof membersCandidate === 'number'
      ? membersCandidate
      : Number(membersCandidate ?? 0)

  const institutionRaw =
    record.institution ??
    record.institutionName ??
    record.organization ??
    record.organizationName ??
    record.institutionId ??
    record.org

  const institution =
    typeof institutionRaw === 'string'
      ? institutionRaw
      : typeof institutionRaw === 'number'
        ? String(institutionRaw)
        : fallbackInstitution

  return {
    category,
    subscriptionAmount: Number.isFinite(amountValue) ? amountValue : 0,
    members: Number.isFinite(membersValue) ? membersValue : 0,
    institution: institution || undefined,
  }
}

function buildCategoryBreakdown(rows: FinancialReport[]): CategoryFinanceSlice[] {
  const slices: CategoryFinanceSlice[] = []

  rows.forEach((row) => {
    const dynamicRow = row as Record<string, unknown>
    const fallbackInstitution =
      typeof dynamicRow.institution === 'string'
        ? dynamicRow.institution
        : typeof dynamicRow.organization === 'string'
          ? dynamicRow.organization
          : undefined

    const candidateArrays = [
      dynamicRow.membershipCategoryBreakdown,
      dynamicRow.categoryBreakdown,
      dynamicRow.subscriptionsByCategory,
      dynamicRow.membershipBreakdown,
      dynamicRow.categorySlices,
      dynamicRow.subscriptionBreakdown,
    ].filter(Array.isArray) as unknown[][]

    candidateArrays.forEach((entries) => {
      entries.forEach((entry) => {
        const normalized = normalizeCategorySlice(entry, fallbackInstitution)
        if (normalized) {
          slices.push(normalized)
        }
      })
    })

    const candidateRecords = [
      dynamicRow.categoryTotals,
      dynamicRow.subscriptionTotals,
    ].filter((value): value is Record<string, unknown> => Boolean(value) && typeof value === 'object')

    candidateRecords.forEach((record) => {
      Object.entries(record).forEach(([category, value]) => {
        const normalized = normalizeCategorySlice(
          typeof value === 'object'
            ? { category, ...(value as Record<string, unknown>) }
            : { category, amount: value },
          fallbackInstitution,
        )
        if (normalized) {
          slices.push(normalized)
        }
      })
    })
  })

  return slices
}

function aggregateCategorySummary(slices: CategoryFinanceSlice[]): CategorySubscriptionSummary[] {
  const map = new Map<string, CategorySubscriptionSummary>()

  slices.forEach((slice) => {
    const existing = map.get(slice.category) ?? {
      category: slice.category,
      subscriptionAmount: 0,
      members: 0,
    }

    existing.subscriptionAmount += slice.subscriptionAmount
    existing.members += slice.members
    map.set(slice.category, existing)
  })

  return Array.from(map.values()).sort((a, b) => b.subscriptionAmount - a.subscriptionAmount)
}

function buildInstitutionOptions(slices: CategoryFinanceSlice[]): string[] {
  const institutions = new Set<string>()
  slices.forEach((slice) => {
    if (slice.institution) {
      institutions.add(slice.institution)
    }
  })
  return Array.from(institutions).sort((a, b) => a.localeCompare(b))
}

// function formatResourceTimestamp(value?: string): string {
//   if (!value) return 'No recent access data'
//   try {
//     const parsed = parseISO(value)
//     if (!isValid(parsed)) return value
//     return format(parsed, 'PP')
//   } catch {
//     return value
//   }
// }

export function AdminReportsPage() {
  const [formRange, setFormRange] = useState<DateRange>(initialRange)
  const [activeRange, setActiveRange] = useState<DateRange>(initialRange)
  const [showValidationError, setShowValidationError] = useState(false)
  const [institutionFilter, setInstitutionFilter] = useState('all')

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat('en-UG', {
        style: 'currency',
        currency: 'UGX',
        maximumFractionDigits: 0,
      }),
    [],
  )
  const numberFormatter = useMemo(
    () => new Intl.NumberFormat('en-UG', { maximumFractionDigits: 0 }),
    [],
  )

  const financeQuery = useFinance<FinancialReport[]>(activeRange, {
    query: {
      select: (response) => response?.data ?? [],
      placeholderData: (previous) => previous,
    },
  })

  const registrationsQuery = useRegistrations<RegistrationStats[]>(activeRange, {
    query: {
      select: (response) => response?.data ?? [],
      placeholderData: (previous) => previous,
    },
  })

  const trainingNeedsQuery = useGetTrainingNeeds<TrainingNeedsReport[]>(activeRange, {
    query: {
      select: (response) => response?.data ?? [],
      placeholderData: (previous) => previous,
    },
  })

  const demographicsQuery = useDemographicsAge<DemographicsSlice[]>({
    query: {
      select: (response) => response?.data ?? [],
      placeholderData: (previous) => previous,
    },
  })
  // const topResourcesParams = useMemo(
  //   () =>
  //     isRangeValid(activeRange)
  //       ? {
  //           from: activeRange.from,
  //           to: activeRange.to,
  //         }
  //       : undefined,
  //   [activeRange],
  // )

  // const topResourcesQuery = useTopResources<Record<string, unknown>[] | undefined>(
  //   topResourcesParams,
  //   {
  //     query: {
  //       enabled: Boolean(topResourcesParams),
  //       select: (response) => response?.data ?? [],
  //       placeholderData: (previous) => previous,
  //     },
  //   },
  // )

  // Essential documents (admin view) - show a small list and allow deletion
  const docsParams = { page: 0, size: 5 }
  const pastDocsQuery = useListAllPastResource(docsParams as any, {
    query: { refetchOnWindowFocus: false },
  })
  const pastDocsResponse = pastDocsQuery.data
  const pastDocsData = pastDocsResponse?.data
  const pastResources = pastDocsData?.content ?? []
  const queryClient = useQueryClient()
  const deletePastResource = useDeletePastResource({}, queryClient)

  const handleDeleteResource = async (resourceId?: number) => {
    if (!resourceId) return
    // simple confirmation for now; can be replaced with a dialog
    if (!window.confirm('Permanently delete this document?')) return
    try {
      await deletePastResource.mutateAsync({ resourceId: resourceId })
      toast.success('Document deleted')
      pastDocsQuery.refetch()
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to delete resource', err)
      toast.error('Failed to delete document')
    }
  }

  const financeRows = financeQuery.data ?? []
  const financeTotals = useMemo(() => {
    const membershipRevenue = financeRows.reduce(
      (sum, row) => sum + (row.membershipRevenue ?? 0),
      0,
    )
    const eventRevenue = financeRows.reduce(
      (sum, row) => sum + (row.eventRevenue ?? 0),
      0,
    )
    const total = financeRows.reduce((sum, row) => {
      const computed = (row.membershipRevenue ?? 0) + (row.eventRevenue ?? 0)
      return sum + (row.total ?? computed)
    }, 0)
    const periods = financeRows.length || 1
    const average = total / periods

    return {
      membershipRevenue,
      eventRevenue,
      total,
      average,
    }
  }, [financeRows])

  const registrationRows = registrationsQuery.data ?? []
  const registrationTotals = useMemo(() => {
    const registrations = registrationRows.reduce(
      (sum, row) => sum + (row.registrations ?? 0),
      0,
    )
    const uniqueMembers = registrationRows.reduce(
      (sum, row) => sum + (row.uniqueUsers ?? 0),
      0,
    )
    const attendancePoints = registrationRows.reduce(
      (sum, row) => sum + (row.attendanceRate ?? 0),
      0,
    )
    const averageAttendance = registrationRows.length
      ? attendancePoints / registrationRows.length
      : 0

    return {
      registrations,
      uniqueMembers,
      averageAttendance,
    }
  }, [registrationRows])

  const trainingNeedsRows = useMemo(
    () =>
      [...(trainingNeedsQuery.data ?? [])].sort(
        (a, b) => (b.count ?? 0) - (a.count ?? 0),
      ),
    [trainingNeedsQuery.data],
  )
  const trainingNeedsCloud = useMemo(
    () => buildTrainingNeedsCloudData(trainingNeedsRows),
    [trainingNeedsRows],
  )

  const demographicsRows = demographicsQuery.data ?? []
  const demographicsTotal = useMemo(
    () => demographicsRows.reduce((sum, slice) => sum + (slice.count ?? 0), 0),
    [demographicsRows],
  )

  const financeChartData = useMemo(
    () => buildFinanceChartData(financeRows),
    [financeRows],
  )
  const demographicsChartData = useMemo(
    () => buildDemographicsChartData(demographicsRows),
    [demographicsRows],
  )
  const registrationCategoryChart = useMemo(
    () => buildRegistrationCategoryChartData(registrationRows),
    [registrationRows],
  )
  const registrationCategoryConfig = useMemo<ChartConfig>(
    () =>
      registrationCategoryChart.categories.reduce((config, category, index) => {
        config[category.key] = {
          label: category.label,
          color: CATEGORY_COLOR_PALETTE[index % CATEGORY_COLOR_PALETTE.length],
        }
        return config
      }, {} as ChartConfig),
    [registrationCategoryChart.categories],
  )
  const hasRegistrationCategoryData =
    registrationCategoryChart.data.length > 0 && registrationCategoryChart.categories.length > 0
  const categoryBreakdown = useMemo(
    () => buildCategoryBreakdown(financeRows),
    [financeRows],
  )
  const institutionOptions = useMemo(
    () => buildInstitutionOptions(categoryBreakdown),
    [categoryBreakdown],
  )
  const filteredCategoryBreakdown = useMemo(
    () =>
      institutionFilter === 'all'
        ? categoryBreakdown
        : categoryBreakdown.filter((slice) => slice.institution === institutionFilter),
    [categoryBreakdown, institutionFilter],
  )
  const categorySummary = useMemo(
    () => aggregateCategorySummary(filteredCategoryBreakdown),
    [filteredCategoryBreakdown],
  )

  // const topResourcesList = useMemo<NormalizedTopResource[]>(() => {
  //   const payload = topResourcesQuery.data
  //   if (!Array.isArray(payload)) return []

  //   const normalized: NormalizedTopResource[] = []

  //   payload.forEach((entry) => {
  //     if (!entry || typeof entry !== 'object') {
  //       return
  //     }
  //     const record = entry as Record<string, unknown>
  //     const label =
  //       typeof record.title === 'string'
  //         ? record.title
  //         : typeof record.resourceName === 'string'
  //           ? record.resourceName
  //           : typeof record.resourceId === 'string'
  //             ? record.resourceId
  //             : typeof record.name === 'string'
  //               ? record.name
  //               : null
  //     if (!label) return

  //     const resourceType =
  //       typeof record.resourceType === 'string'
  //         ? record.resourceType
  //         : typeof record.type === 'string'
  //           ? record.type
  //           : 'Resource'
  //     const countCandidate =
  //       typeof record.accessCount === 'number'
  //         ? record.accessCount
  //         : typeof record.count === 'number'
  //           ? record.count
  //           : typeof record.total === 'number'
  //             ? record.total
  //             : typeof record.value === 'number'
  //               ? record.value
  //               : 0
  //     const lastAccessed =
  //       typeof record.lastAccessed === 'string'
  //         ? record.lastAccessed
  //         : typeof record.updatedAt === 'string'
  //           ? record.updatedAt
  //           : undefined
  //     const idCandidate =
  //       typeof record.resourceId === 'string'
  //         ? record.resourceId
  //         : typeof record.id === 'string'
  //           ? record.id
  //           : label

  //     normalized.push({
  //       id: idCandidate,
  //       label,
  //       resourceType,
  //       count: Number(countCandidate) || 0,
  //       lastAccessed,
  //     })
  //   })

  //   return normalized.sort((a, b) => b.count - a.count).slice(0, 5)
  // }, [topResourcesQuery.data])

  useEffect(() => {
    if (institutionFilter === 'all') return
    if (!institutionOptions.includes(institutionFilter)) {
      setInstitutionFilter('all')
    }
  }, [institutionFilter, institutionOptions])

  const hasAnyError =
    financeQuery.isError ||
    registrationsQuery.isError ||
    trainingNeedsQuery.isError ||
    demographicsQuery.isError

  const onSubmitRange = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!isRangeValid(formRange)) {
      setShowValidationError(true)
      return
    }

    setActiveRange(formRange)
    setShowValidationError(false)
  }

  const handleQuickRange = (range: DateRange) => {
    setFormRange(range)
    setActiveRange(range)
    setShowValidationError(false)
  }

  const quickRanges: { label: string; range: DateRange }[] = [
    {
      label: 'Last 30 days',
      range: {
        from: format(subDays(today, 29), 'yyyy-MM-dd'),
        to: format(today, 'yyyy-MM-dd'),
      },
    },
    {
      label: 'Last 90 days',
      range: {
        from: format(subDays(today, 89), 'yyyy-MM-dd'),
        to: format(today, 'yyyy-MM-dd'),
      },
    },
    {
      label: 'Year to date',
      range: {
        from: format(startOfYear(today), 'yyyy-MM-dd'),
        to: format(today, 'yyyy-MM-dd'),
      },
    },
  ]

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h1 className="bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-3xl font-semibold tracking-tight text-transparent">
          Reports & insights
        </h1>
        <p className="text-muted-foreground max-w-2xl text-sm">
          Review membership performance, financial health, and training needs for the selected period.
        </p>
      </div>

      <Card className="border-muted">
        <CardHeader className="pb-3">
          <CardTitle>Date range</CardTitle>
          <CardDescription>
            The selected range applies to finance, registration, and training reports. Demographics always reflect the latest snapshot.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <form
            onSubmit={onSubmitRange}
            className="grid gap-4 md:grid-cols-[repeat(3,minmax(0,1fr))] md:items-end"
          >
            <div className="flex flex-col gap-2">
              <label htmlFor="from" className="text-sm font-medium">
                From
              </label>
              <Input
                id="from"
                name="from"
                type="date"
                value={formRange.from}
                max={formRange.to || undefined}
                onChange={(event) =>
                  setFormRange((previous) => ({
                    ...previous,
                    from: event.target.value,
                  }))
                }
              />
            </div>
            <div className="flex flex-col gap-2">
              <label htmlFor="to" className="text-sm font-medium">
                To
              </label>
              <Input
                id="to"
                name="to"
                type="date"
                value={formRange.to}
                min={formRange.from || undefined}
                max={format(today, 'yyyy-MM-dd')}
                onChange={(event) =>
                  setFormRange((previous) => ({
                    ...previous,
                    to: event.target.value,
                  }))
                }
              />
            </div>
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-end">
              <Button type="submit" className="w-full md:w-auto">
                Apply range
              </Button>
            </div>
          </form>
          {showValidationError && (
            <div
              role="alert"
              className="border-destructive/40 bg-destructive/10 text-destructive flex items-center gap-2 rounded-md border px-3 py-2 text-sm"
            >
              <AlertTriangle className="size-4 shrink-0" aria-hidden />
              <span>Select a valid date range before applying.</span>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {quickRanges.map(({ label, range }) => {
              const isActive =
                range.from === activeRange.from && range.to === activeRange.to

              return (
                <Button
                  key={label}
                  type="button"
                  variant={isActive ? 'secondary' : 'outline'}
                  onClick={() => handleQuickRange(range)}
                >
                  {label}
                </Button>
              )
            })}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          icon={LineChartIcon}
          label="Total revenue"
          value={formatCurrency(financeTotals.total, currencyFormatter)}
          helper={`${activeRange.from} → ${activeRange.to}`}
          tone="primary"
        />
        <MetricCard
          icon={BarChart3}
          label="Membership subscriptions"
          value={formatCurrency(financeTotals.membershipRevenue, currencyFormatter)}
          helper="Subscription dues and renewals"
          tone="emerald"
        />
        <MetricCard
          icon={BarChart3}
          label="Event revenue"
          value={formatCurrency(financeTotals.eventRevenue, currencyFormatter)}
          helper="Event registrations & fees"
          tone="amber"
        />
        <MetricCard
          icon={Target}
          label="Registrations"
          value={numberFormatter.format(registrationTotals.registrations)}
          helper={`${numberFormatter.format(registrationTotals.uniqueMembers)} unique members`}
          tone="violet"
        />
      </div>

      <Card className="border-muted">
        <CardHeader className="pb-0">
          <CardTitle>Subscriptions by membership category</CardTitle>
          <CardDescription>
            Visualise subscription amounts and member counts per category, calibrated by institution when data is available.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm font-medium">Institution calibration</p>
              <p className="text-xs text-muted-foreground">
                Filter the breakdown to a specific institution or view the aggregate portfolio.
              </p>
            </div>
            <Select
              value={institutionFilter}
              onValueChange={setInstitutionFilter}
              disabled={!institutionOptions.length}
            >
              <SelectTrigger className="w-full lg:w-[280px]">
                <SelectValue placeholder="All institutions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All institutions</SelectItem>
                {institutionOptions.map((institution) => (
                  <SelectItem key={institution} value={institution}>
                    {institution}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {financeQuery.isPending && (
            <div className="mt-6">
              <Skeleton className="bg-muted h-[320px] w-full rounded-lg" />
            </div>
          )}
          {!financeQuery.isPending && categorySummary.length === 0 && (
            <div className="mt-6">
              <EmptyState message="The finance API did not return category-level subscription data for this period." />
            </div>
          )}
          {!financeQuery.isPending && categorySummary.length > 0 && (
            <div className="mt-6 grid gap-6 lg:grid-cols-[3fr_2fr]">
              <ChartContainer
                className="h-[320px] w-full rounded-xl border border-secondary/20 bg-secondary/5"
                config={{
                  subscriptionAmount: {
                    label: 'Subscription amount',
                    color: 'var(--color-chart-1)',
                  },
                }}
              >
                <BarChart
                  data={categorySummary}
                  barSize={32}
                  margin={{ top: 12, right: 16, left: 0, bottom: 24 }}
                >
                  <CartesianGrid vertical={false} strokeDasharray="4 8" />
                  <XAxis dataKey="category" tickLine={false} axisLine={false} />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) =>
                      currencyFormatter
                        .format(typeof value === 'number' ? value : Number(value))
                        .replace(/UGX\s?/, '')
                    }
                  />
                  <ChartTooltip
                    cursor={false}
                    content={
                      <ChartTooltipContent
                        formatter={(value) => (
                          <div className="flex items-center justify-between gap-4">
                            <span className="text-muted-foreground">Subscriptions</span>
                            <span className="font-medium">
                              {formatCurrency(
                                typeof value === 'number' ? value : Number(value),
                                currencyFormatter,
                              )}
                            </span>
                          </div>
                        )}
                      />
                    }
                  />
                  <Bar
                    dataKey="subscriptionAmount"
                    fill="var(--color-chart-1)"
                    radius={[6, 6, 0, 0]}
                  />
                </BarChart>
              </ChartContainer>
              <div className="overflow-hidden rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Members</TableHead>
                      <TableHead className="text-right">Subscriptions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {categorySummary.map((row) => (
                      <TableRow key={row.category}>
                        <TableCell className="font-medium">{row.category}</TableCell>
                        <TableCell className="text-right">
                          {numberFormatter.format(row.members)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(row.subscriptionAmount, currencyFormatter)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {hasAnyError && (
        <div
          role="alert"
          className="border-destructive/40 bg-destructive/5 text-destructive flex items-center gap-3 rounded-lg border px-4 py-3 text-sm"
        >
          <AlertTriangle className="size-4 shrink-0" aria-hidden />
          <div>
            <p className="font-medium">We could not load the latest reports.</p>
            <p className="text-muted-foreground text-xs">
              Please refresh the page or try adjusting the filters.
            </p>
          </div>
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-[2fr_1fr]">
        <Card className="border-muted">
          <CardHeader className="pb-0">
            <CardTitle>Financial trend</CardTitle>
            <CardDescription>
              Monthly revenue split by membership subscriptions and event income.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {financeQuery.isPending && (
              <Skeleton className="bg-muted h-[260px] w-full rounded-lg" />
            )}
            {!financeQuery.isPending && financeChartData.length === 0 && (
              <EmptyState message="No financial records in this range yet." />
            )}
            {!financeQuery.isPending && financeChartData.length > 0 && (
              <ChartContainer
                className="h-[260px] w-full rounded-xl border border-primary/15 bg-primary/5"
                config={{
                  membershipRevenue: {
                    label: 'Membership subscriptions',
                    color: 'var(--color-chart-1)',
                  },
                  eventRevenue: {
                    label: 'Event revenue',
                    color: 'var(--color-chart-2)',
                  },
                  total: {
                    label: 'Total revenue',
                    color: 'var(--color-chart-3)',
                  },
                }}
              >
                <LineChart
                  data={financeChartData}
                  margin={{ top: 12, right: 24, bottom: 0, left: 0 }}
                >
                  <CartesianGrid vertical={false} strokeDasharray="4 8" />
                  <XAxis dataKey="period" tickLine={false} axisLine={false} />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) =>
                      currencyFormatter
                        .format(typeof value === 'number' ? value : Number(value))
                        .replace(/UGX\s?/, '')
                    }
                  />
                  <ChartTooltip
                    cursor={false}
                    content={
                      <ChartTooltipContent
                        formatter={(value, name) => (
                          <div className="flex items-center justify-between gap-4">
                            <span className="text-muted-foreground">{name}</span>
                            <span className="font-medium">
                              {formatCurrency(
                                typeof value === 'number' ? value : Number(value),
                                currencyFormatter,
                              )}
                            </span>
                          </div>
                        )}
                      />
                    }
                  />
                  <ChartLegend content={<ChartLegendContent className="pt-2" />} />
                  <Line
                    type="monotone"
                    dataKey="membershipRevenue"
                    stroke="var(--color-chart-1)"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="eventRevenue"
                    stroke="var(--color-chart-2)"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="total"
                    stroke="var(--color-chart-3)"
                    strokeWidth={2}
                    strokeDasharray="6 6"
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                </LineChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>

        <Card className="border-muted">
          <CardHeader className="pb-0">
            <CardTitle>Attendance health</CardTitle>
            <CardDescription>
              Average attendance across registrations in the selected period.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {registrationsQuery.isPending && (
              <Skeleton className="bg-muted h-[80px] w-full rounded-lg" />
            )}
            {!registrationsQuery.isPending && (
              <div className="flex flex-col gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Average attendance rate</p>
                  <p className="text-2xl font-semibold">
                    {registrationTotals.averageAttendance.toFixed(1)}%
                  </p>
                </div>
                <Progress value={Math.min(100, registrationTotals.averageAttendance)} />
                <p className="text-xs text-muted-foreground">
                  Attendance is calculated as the mean across all periods returned by the API.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.2fr_1fr]">
        <Card className="border-muted">
          <CardHeader className="pb-0">
            <CardTitle>Registrations by period</CardTitle>
            <CardDescription>
              Track how each membership category contributes to registrations over time.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {registrationsQuery.isPending && (
              <Skeleton className="bg-muted h-[260px] w-full rounded-lg" />
            )}
            {!registrationsQuery.isPending && !hasRegistrationCategoryData && (
              <EmptyState message="No membership category breakdown is available for this window." />
            )}
            {!registrationsQuery.isPending && hasRegistrationCategoryData && (
              <ChartContainer
                className="h-[260px] w-full rounded-xl border border-emerald-500/15 bg-emerald-500/5"
                config={registrationCategoryConfig}
              >
                <BarChart
                  data={registrationCategoryChart.data}
                  barSize={28}
                  margin={{ top: 12, right: 16, left: 0, bottom: 0 }}
                >
                  <CartesianGrid vertical={false} strokeDasharray="4 8" />
                  <XAxis dataKey="period" tickLine={false} axisLine={false} />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) =>
                      numberFormatter.format(
                        typeof value === 'number' ? value : Number(value),
                      )
                    }
                  />
                  <ChartTooltip
                    cursor={false}
                    content={
                      <ChartTooltipContent
                        formatter={(value, name) => (
                          <div className="flex items-center justify-between gap-4">
                            <span className="text-muted-foreground">{name}</span>
                            <span className="font-medium">
                              {numberFormatter.format(
                                typeof value === 'number' ? value : Number(value),
                              )}
                            </span>
                          </div>
                        )}
                      />
                    }
                  />
                  <ChartLegend content={<ChartLegendContent className="pt-2" />} />
                  {registrationCategoryChart.categories.map((category, index) => (
                    <Bar
                      key={category.key}
                      dataKey={category.key}
                      stackId="registrations"
                      fill={CATEGORY_COLOR_PALETTE[index % CATEGORY_COLOR_PALETTE.length]}
                      radius={
                        index === registrationCategoryChart.categories.length - 1
                          ? [6, 6, 0, 0]
                          : [0, 0, 0, 0]
                      }
                    />
                  ))}
                </BarChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>

        <Card className="border-muted">
          <CardHeader className="pb-0">
            <CardTitle>Demographics snapshot</CardTitle>
            <CardDescription>Latest member distribution by age band.</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {demographicsQuery.isPending && (
              <Skeleton className="bg-muted h-[260px] w-full rounded-lg" />
            )}
            {!demographicsQuery.isPending && demographicsChartData.length === 0 && (
              <EmptyState message="Demographic data is not available yet." />
            )}
            {!demographicsQuery.isPending && demographicsChartData.length > 0 && (
              <div className="flex flex-col gap-4">
                <div className="grid gap-2 text-sm">
                  {demographicsChartData.map((slice) => {
                    const count = slice.count ?? 0
                    const percentage = demographicsTotal
                      ? (count / demographicsTotal) * 100
                      : 0
                    return (
                      <div
                        key={slice.dimension}
                        className="flex flex-col gap-1 rounded-md border border-dashed border-muted p-3"
                      >
                        <div className="flex items-center justify-between text-sm font-medium">
                          <span>{slice.dimension}</span>
                          <span>{numberFormatter.format(count)}</span>
                        </div>
                        <Progress value={Math.min(100, Number(percentage.toFixed(1)))} />
                        <span className="text-muted-foreground text-xs">
                          {percentage.toFixed(1)}% of members
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="border-muted">
        <CardHeader className="pb-0">
          <CardTitle>Training needs</CardTitle>
          <CardDescription>
            Highlights the most requested development areas captured during the selected period.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          {trainingNeedsQuery.isPending && (
            <Skeleton className="bg-muted h-[220px] w-full rounded-lg" />
          )}
          {!trainingNeedsQuery.isPending && trainingNeedsRows.length === 0 && (
            <EmptyState message="No training needs were recorded for this range." />
          )}
          {!trainingNeedsQuery.isPending && trainingNeedsRows.length > 0 && (
            <div className="rounded-lg border border-dashed border-primary/20 bg-primary/5 p-6">
              <div className="flex flex-col items-center gap-6">
                {trainingNeedsCloud[0] && (
                  <div className="text-center">
                    <span
                      className="text-primary leading-tight"
                      style={{
                        display: 'inline-block',
                        fontSize: `${Math.max(2.6, trainingNeedsCloud[0].weight + 1)}rem`,
                        fontWeight: 800,
                      }}
                      title={`${trainingNeedsCloud[0].label}: ${numberFormatter.format(trainingNeedsCloud[0].count)} responses`}
                    >
                      {trainingNeedsCloud[0].label}
                    </span>
                  </div>
                )}
                <div className="flex flex-wrap items-center justify-center gap-4">
                  {trainingNeedsCloud.slice(1).map((item) => (
                    <span
                      key={item.label}
                      className="text-primary transition-opacity duration-200 hover:opacity-100"
                      style={{
                        fontSize: `${item.weight}rem`,
                        opacity: 0.65 + Math.min(item.weight / 2.5, 0.35),
                        fontWeight: item.weight > 1.5 ? 700 : 500,
                      }}
                      title={`${item.label}: ${numberFormatter.format(item.count)} responses`}
                    >
                      {item.label}
                    </span>
                  ))}
                </div>
              </div>
              <p className="text-muted-foreground mt-4 text-center text-xs">
                Word size scales with the number of responses captured for each skill gap.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
      <Card className="border-muted">
        <CardHeader className="pb-0">
          <CardTitle>Essential documents</CardTitle>
          <CardDescription>
            Quick access to important documents. Admins can remove outdated items.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          {pastDocsQuery.isLoading && (
            <Skeleton className="bg-muted h-[160px] w-full rounded-lg" />
          )}
          {!pastDocsQuery.isLoading && pastResources.length === 0 && (
            <EmptyState message="No essential documents available." />
          )}
          {pastResources.length > 0 && (
            <div className="space-y-3">
              {pastResources.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-primary/20 bg-primary/5 p-4"
                >
                  <div className="min-w-0">
                    <div className="font-medium truncate">{doc.title}</div>
                    <div className="text-sm text-muted-foreground truncate">
                      {doc.category || ''}
                      {doc.owner ? ` · ${doc.owner}` : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {doc.preview?.url && (
                      <Button variant="ghost" size="sm">
                        <a href={doc.preview.url} target="_blank" rel="noreferrer">Open</a>
                      </Button>
                    )}
                    <Button size="sm" variant="destructive" onClick={() => handleDeleteResource(doc.id)}>
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

type MetricTone = 'primary' | 'emerald' | 'amber' | 'violet' | 'neutral'

const METRIC_TONE_STYLES: Record<MetricTone, {
  card: string
  iconWrap: string
  value: string
  helper: string
}> = {
  primary: {
    card: 'border-primary/20 bg-primary/5',
    iconWrap: 'bg-primary/15 text-primary',
    value: 'text-primary',
    helper: 'text-primary/70',
  },
  emerald: {
    card: 'border-emerald-500/20 bg-emerald-500/10',
    iconWrap: 'bg-emerald-500/15 text-emerald-600',
    value: 'text-emerald-600',
    helper: 'text-emerald-600/70',
  },
  amber: {
    card: 'border-amber-500/20 bg-amber-500/10',
    iconWrap: 'bg-amber-500/15 text-amber-600',
    value: 'text-amber-600',
    helper: 'text-amber-600/80',
  },
  violet: {
    card: 'border-violet-500/20 bg-violet-500/10',
    iconWrap: 'bg-violet-500/15 text-violet-600',
    value: 'text-violet-600',
    helper: 'text-violet-600/70',
  },
  neutral: {
    card: 'border-muted',
    iconWrap: 'bg-primary/10 text-primary',
    value: '',
    helper: 'text-muted-foreground',
  },
}

function MetricCard({
  icon: Icon,
  label,
  value,
  helper,
  tone = 'neutral',
}: {
  icon: typeof LineChartIcon
  label: string
  value: string
  helper: string
  tone?: MetricTone
}) {
  const styles = METRIC_TONE_STYLES[tone] ?? METRIC_TONE_STYLES.neutral

  return (
    <Card className={cn('border transition-colors duration-300', styles.card)}>
      <CardContent className="flex flex-col gap-3 p-5">
        <div className="flex items-center gap-3 text-sm font-medium">
          <div className={cn('flex size-9 items-center justify-center rounded-md', styles.iconWrap)}>
            <Icon className="size-4" aria-hidden />
          </div>
          <span>{label}</span>
        </div>
        <p className={cn('text-2xl font-semibold tracking-tight', styles.value)}>{value}</p>
        <p className={cn('text-xs', styles.helper)}>{helper}</p>
      </CardContent>
    </Card>
  )
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex h-[180px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-primary/20 bg-primary/5 p-6 text-center text-sm text-primary/80 dark:border-primary/30">
      <ChartPie className="size-6 text-primary" aria-hidden />
      <p>{message}</p>
    </div>
  )
}

export default AdminReportsPage
